import { useMsal } from '@azure/msal-react';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil';

import { DICOMApprovalStatus } from '~/domain/dicom/dicomstructureset';
import { StructureSet } from '~/domain/patient/structureset';
import { environmentState } from '~/state/atoms/engineAtoms';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';

import { roiState } from '~/state/atoms/structureSet/roiAtoms';
import {
    currentStructureSetState,
    structureSetState,
} from '~/state/atoms/structureSet/structureSetAtoms';

/**
 * Hook to manage structure set-related state in Recoil.
 *
 * This hook initializes and manages structure set data based on either:
 * 1. A provided `StructureSet` instance (parameter `structureSetInstance`), or
 * 2. The globally managed `currentStructureSetState` if `structureSetInstance` is not provided.
 *
 * @param {StructureSet} [structureSetInstance] - Optional structure set instance to use for initializing and managing state.
 * @returns {object} - An object containing the structure set data and associated state management methods.
 */
export default function useStructureSet(structureSetInstance?: StructureSet) {
    const sessionData = useRecoilValue(sessionDataAtom);
    const { instance } = useMsal();

    const environment = useRecoilValue(environmentState);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const activeStructureSet = structureSetInstance || currentStructureSet;
    const [structureSet, setStructureSet] = useRecoilState(
        structureSetState(
            activeStructureSet ? activeStructureSet.dicomUid : 'default'
        )
    );

    useEffect(() => {
        if (!structureSet && activeStructureSet) {
            setStructureSet({
                sopUid: activeStructureSet.dicomUid,
                structureSetLabel: activeStructureSet.structureSetLabel,
                structureSetDescription:
                    activeStructureSet.structureSetDescription,
                approvalStatus: activeStructureSet.approvalStatus,
                reviewerName: activeStructureSet.dicomStructureSet.reviewerName,
                reviewDate: activeStructureSet.dicomStructureSet.reviewDate,
                reviewTime: activeStructureSet.dicomStructureSet.reviewTime,
                visible: activeStructureSet.visible,
                manufacturer: activeStructureSet.manufacturer,
                manufacturerModelName: activeStructureSet.manufacturerModelName,
                softwareVersions: activeStructureSet.softwareVersions,
                numberOfStructures: activeStructureSet.numberOfStructures,
            });
            setStructureSetApprovalStatus(
                activeStructureSet.approvalStatus,
                activeStructureSet.reviewerName.toString()
            );
            setRoisApprovalStatus(activeStructureSet.approvalStatus);
        }
    }, [activeStructureSet]);

    const setStructureSetLabel = useCallback(
        (newLabel: string) => {
            if (activeStructureSet) {
                try {
                    activeStructureSet.structureSetLabel = newLabel; // Update domain object
                    setStructureSet((prev) => ({
                        ...prev,
                        structureSetLabel: newLabel,
                    }));
                } catch (error) {
                    console.error(
                        `Error updating structure set label: ${error}`
                    );
                }
            }
        },
        [activeStructureSet, setStructureSet]
    );

    const setStructureSetDescription = useCallback(
        (newDescription: string) => {
            if (activeStructureSet) {
                try {
                    activeStructureSet.structureSetDescription = newDescription;
                    setStructureSet((prev) => ({
                        ...prev,
                        structureSetDescription: newDescription,
                    }));
                } catch (error) {
                    console.error(
                        `Error updating structure set description: ${error}`
                    );
                }
            }
        },
        [activeStructureSet, setStructureSet]
    );

    const setStructureSetVisibility = useCallback(
        (newVisibility: boolean) => {
            if (activeStructureSet) {
                try {
                    activeStructureSet.visible = newVisibility;
                    setStructureSet((prev) => ({
                        ...prev,
                        visible: newVisibility,
                    }));
                } catch (error) {
                    console.error(`Error updating visibility: ${error}`);
                }
            }
        },
        [activeStructureSet]
    );

    const setRoisVisibility = useCallback(
        (newVisibility: boolean) => {
            if (environment) {
                try {
                    environment.setRoisVisibility(newVisibility);
                } catch (error) {
                    console.error(`Error updating ROIs visibility: ${error}`);
                }
            }
        },
        [environment]
    );

    const setStructureSetApprovalStatus = useCallback(
        (newStatus: DICOMApprovalStatus, newReviewerName?: string) => {
            if (activeStructureSet) {
                try {
                    const activeMsalAccount =
                        instance?.getActiveAccount()?.name;
                    const reviewer =
                        newReviewerName ??
                        sessionData?.username ??
                        activeMsalAccount ??
                        'unknown';

                    const {
                        approvalStatus,
                        reviewerName,
                        reviewDate,
                        reviewTime,
                    } = activeStructureSet.setStructureSetApprovalStatus(
                        newStatus,
                        reviewer
                    );

                    setStructureSet((prev) => ({
                        ...prev,
                        approvalStatus,
                        reviewerName,
                        reviewDate,
                        reviewTime,
                    }));
                } catch (error) {
                    console.error(`Error updating approval status: ${error}`);
                }
            }
        },
        [activeStructureSet, setStructureSet]
    );

    const setRoisApprovalStatus = useCallback(
        (newStatus: DICOMApprovalStatus) => {
            if (activeStructureSet) {
                try {
                    activeStructureSet.setRoisApprovalStatus(newStatus);
                } catch (error) {
                    console.error(
                        `Error updating ROIs approval status: ${error}`
                    );
                }
            }
        },
        [activeStructureSet]
    );

    const deleteRoi = useRecoilCallback(
        ({ set }) =>
            (roiNumber: number, sopUid: string) => {
                try {
                    environment?.deleteRoi(roiNumber);
                    set(roiState({ roiNumber, sopUid }), undefined);
                } catch (error) {
                    enqueueSnackbar(`Error deleting ROI: ${error}`, {
                        variant: 'warning',
                    });
                }
            },
        [structureSetInstance, setStructureSet, environment]
    );

    return {
        structureSet,
        setStructureSetLabel,
        setStructureSetDescription,
        setStructureSetApprovalStatus,
        deleteRoi,
        setRoisApprovalStatus,
        setStructureSetVisibility,
        setRoisVisibility,
    };
}
